'use client';

import { c as _c } from "react/compiler-runtime";
import { ipfsImageUrl, toCustomGateway, toCustomGatewayOrNull } from '@morfi/backend-sdk-v2/client';
import { useReducedMotion } from 'motion/react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { Component, type CSSProperties, type ReactNode, type RefObject, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Skeleton } from '@/components/ui/app-skeleton';
import { Icon } from '@/components/ui/icon';
import { Loading03Icon } from '@/components/ui/icon/glyphs';
import type { OutcomeHueName } from '@/config/outcome-colors';
import { useIntersectionObserver } from '@/hooks/ui/use-intersection-observer';
import { encodeManifestHash, generateDotStudioCoverPng, type ManifestThemeName, manifestForMarket, manifestForOutcome, manifestForUser, manifestForUserCover, manifestForWinner, type PaletteRef, type PatternManifest, type PatternParams, renderManifestToBlob, resolveHeroCoverPattern } from '@/lib/pattern-studio';
import { sendLog } from '@/lib/telemetry/client';
import { EASE_DEFAULT_CSS } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';
import { useLightboxStore } from '@/stores/lightbox';
import { useNsfwRevealStore } from '@/stores/nsfw-reveal';
import { NsfwOverlay } from './nsfw-overlay';

// ─────────────────────────────────────────────────────────────────────────
// LOAD-ANIMATION KNOBS — tweak everything from here.
// ─────────────────────────────────────────────────────────────────────────
// All numeric / string knobs that drive the loading skeleton + image
// entrance live in one place so they can be tuned without spelunking the
// component body. Hard rules:
//   - The Tailwind classes in `imageFromClasses*` MUST be written as
//     literal strings (Tailwind v4's compiler scans source text — template
//     interpolation is invisible to it).
//   - Reduced-motion users skip the inline-style transition entirely and
//     render in the settled state immediately.
const LOAD_KNOBS = {
  // ── Image entrance animation ────────────────────────────────────────
  // Per-property durations (ms). The blur duration dominates the felt
  // motion — bump it for a slower defocus reveal.
  durationOpacityMs: 600,
  durationTransformMs: 700,
  durationBlurMs: 700,
  // Easing curves for each property.
  //   - transform: back-out (gentle overshoot at the end so it "settles")
  //   - filter:    quartic deceleration (front-loaded so blur clears smoothly)
  easingOpacity: EASE_DEFAULT_CSS,
  easingTransform: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  easingBlur: 'cubic-bezier(0.22, 1, 0.36, 1)',
  // Per-variant from-state Tailwind classes. The to-state is always
  // `scale-100 opacity-100 blur-0`. Bump blur for a more dramatic reveal,
  // drop scale further (e.g. `scale-90`) for a bigger "pop in".
  imageFromClassesThumbnail: 'scale-95 opacity-0 blur-lg',
  imageFromClassesAvatar: 'scale-90 opacity-0 blur-lg',
  imageFromClassesHero: 'scale-[0.97] opacity-0 blur-xl',
  // ── Shimmer skeleton ────────────────────────────────────────────────
  // Highlight is `--foreground-tertiary` mixed at this % over the
  // `--image-halftone-surface` base. 8–15% is "subtle"; 20–35% is "obvious".
  shimmerHighlightPct: 12,
  // Gradient band width. Stops are symmetric around 50%; smaller spread
  // (e.g. 45 / 55) = thinner / sharper highlight, wider (35 / 65) = softer.
  shimmerBandStartPct: 42,
  shimmerBandEndPct: 58,
  // Full sweep loop length — slower feels gentler.
  shimmerSweepSeconds: 2.2,
  // How fast the skeleton fades out once the image has loaded.
  shimmerFadeOutMs: 600
} as const;

// Hint the compositor — these properties animate together on every load.
const IMAGE_LOAD_TRANSITION_STYLE: CSSProperties = {
  transitionProperty: 'opacity, transform, filter',
  transitionDuration: `${String(LOAD_KNOBS.durationOpacityMs)}ms, ${String(LOAD_KNOBS.durationTransformMs)}ms, ${String(LOAD_KNOBS.durationBlurMs)}ms`,
  transitionTimingFunction: `${LOAD_KNOBS.easingOpacity}, ${LOAD_KNOBS.easingTransform}, ${LOAD_KNOBS.easingBlur}`,
  willChange: 'opacity, transform, filter'
};

// Soft reveal — opacity-only over 300 ms. Used by `loadReveal: 'soft'` for
// surfaces where the default scale/blur tween is too heavy (e.g. the hover
// raster overlay in `MarketRowCard`, where a parent wrapper already owns
// the dominant opacity crossfade). Matches the 300 ms feel of a light
// dissolve without re-introducing the 600–700 ms scale/blur reveal.
const IMAGE_LOAD_TRANSITION_STYLE_SOFT: CSSProperties = {
  transitionProperty: 'opacity',
  transitionDuration: '300ms',
  transitionTimingFunction: EASE_DEFAULT_CSS,
  willChange: 'opacity'
};
const SKELETON_FADE_OUT_STYLE: CSSProperties = {
  transitionProperty: 'opacity',
  transitionDuration: `${String(LOAD_KNOBS.shimmerFadeOutMs)}ms`,
  transitionTimingFunction: EASE_DEFAULT_CSS
};

// Component-scoped shimmer keyframe. Lives here (rather than globals.css)
// because it's only ever consumed by `<OptimizedImage>`'s loading skeleton —
// keeping it co-located avoids polluting the design-system stylesheet with
// a single-use animation. React 19 hoists `<style precedence="default"
// href="...">` to the document head and dedupes by `href`, so rendering it
// inside every OptimizedImage instance is free.
const SHIMMER_STYLE_HREF: string = 'morfi-image-shimmer';
const SHIMMER_STYLE_CSS: string = `
@keyframes morfi-image-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
.morfi-image-shimmer {
  background-color: var(--image-halftone-surface);
  background-image: linear-gradient(
    110deg,
    transparent ${String(LOAD_KNOBS.shimmerBandStartPct)}%,
    color-mix(in oklch, var(--foreground-tertiary) ${String(LOAD_KNOBS.shimmerHighlightPct)}%, transparent) 50%,
    transparent ${String(LOAD_KNOBS.shimmerBandEndPct)}%
  );
  background-size: 200% 100%;
  background-repeat: no-repeat;
  animation: morfi-image-shimmer ${String(LOAD_KNOBS.shimmerSweepSeconds)}s linear infinite;
}
.morfi-image-shimmer.morfi-image-shimmer-avatar {
  background-color: var(--background-tertiary);
}
/* Invisible skeletons must not invalidate page styles on every chart frame. */
.morfi-image-shimmer.opacity-0,
.morfi-image-skeleton-offscreen {
  animation-play-state: paused;
}
@media (prefers-reduced-motion: reduce) {
  .morfi-image-shimmer {
    animation: none;
    background-image: none;
  }
}
`.trim();
function ShimmerStyle() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <style precedence="default" href={SHIMMER_STYLE_HREF}>{SHIMMER_STYLE_CSS}</style>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/**
 * Standalone OptimizedImage skeleton — the same shimmer the loaded-state
 * fades out, surfaced for callers that need the loading visual without an
 * actual image to render (e.g. waiting on an upstream fetch / generation).
 * Fills its parent via `absolute inset-0`; parent owns the box + radius.
 */
export function ImageShimmer(t0) {
  const $ = _c(5);
  const {
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <ShimmerStyle />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== className) {
    t2 = cn("morfi-image-shimmer absolute inset-0", className);
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <>{t1}<div className={t2} aria-hidden="true" /></>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

/**
 * Viewport gate for image fetching. Returns a ref to attach to the image's
 * outer wrapper plus a `shouldRender` boolean. Until the wrapper is near
 * the viewport (rootMargin 100px) AND `priority` / `preview` aren't set,
 * `shouldRender` stays false — consumers translate that into `src=null`
 * to defer the fetch. Once the observer flips intersecting, the boolean
 * latches sticky-true so scroll-out doesn't re-gate.
 *
 * The ref is returned wrapped in an object on purpose: the React Compiler
 * forbids passing bare ref values to non-component functions, but property
 * access from a hook return object (mirrors `useImageLoadState.imgRef` at
 * the call sites below) is opaque to the tracker.
 */
function useViewportGate(opts) {
  const $ = _c(7);
  const containerRef = useRef(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      rootMargin: "100px"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const {
    isIntersecting
  } = useIntersectionObserver(containerRef, t0);
  const [hasIntersected, setHasIntersected] = useState(false);
  let t1;
  let t2;
  if ($[1] !== hasIntersected || $[2] !== isIntersecting) {
    t1 = () => {
      if (isIntersecting && !hasIntersected) {
        setHasIntersected(true);
      }
    };
    t2 = [isIntersecting, hasIntersected];
    $[1] = hasIntersected;
    $[2] = isIntersecting;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  const t3 = opts.priority || opts.preview || hasIntersected;
  let t4;
  if ($[5] !== t3) {
    t4 = {
      containerRef,
      shouldRender: t3
    };
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function useImageLoadState(src) {
  const $ = _c(7);
  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [instant, setInstant] = useState(false);
  const [prevSrc, setPrevSrc] = useState(src);
  if (prevSrc !== src) {
    setPrevSrc(src);
    setLoaded(false);
    setInstant(false);
  }
  let t0;
  let t1;
  if ($[0] !== instant) {
    t0 = () => {
      if (instant) {
        return;
      }
      const el = imgRef.current;
      if (el?.complete && el.naturalWidth > 0) {
        setInstant(true);
        setLoaded(true);
      }
    };
    t1 = [instant];
    $[0] = instant;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useLayoutEffect(t0, t1);
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setLoaded(true);
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== instant || $[5] !== loaded) {
    t3 = {
      loaded,
      instant,
      imgRef,
      handleLoad: t2
    };
    $[4] = instant;
    $[5] = loaded;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}

// Backoff schedule for transient image-fetch failures. The AI proposal
// pipeline emits a *predicted* IPFS gateway URL the instant it computes the
// CID — before the background Pinata pin has landed. During that window the
// gateway returns 403, and a plain `<Image>` gives up permanently (the
// broken-image glyph sticks). Re-requesting with a neutral cache-bust query a
// moment later picks up the now-pinned bytes.
//
// The total window MUST outlast the backend's own pin retry (pipeline.ts
// `BG_PIN_RETRY_DELAYS_MS` = 0.5s/1.5s/4s + upload durations ≈ up to ~12s in the
// worst recoverable case). A larger image (often the cover) pins slower and may
// need a backend retry, landing at ~8-12s — if the client quit at ~7s it would
// wrongly show "failed" for an image that is about to exist. So we retry out to
// ~18s. Each entry is the delay (ms) BEFORE the corresponding retry; once the
// list is exhausted the failure is permanent and surfaced to `onError`.
const IMAGE_RETRY_DELAYS_MS = [600, 1200, 2200, 3500, 5000, 6000] as const;
/** Hero renders up to ~640 CSS px at 3× DPR; caps full-resolution IPFS originals. */
const HERO_MAX_WIDTH_PX = 2048;

/**
 * Transient-failure retry for the legacy `src` path. Returns the URL to render
 * (cache-busted on retries so the browser / Next optimizer refetch instead of
 * replaying the cached error), an error handler that schedules the next retry,
 * and a success handler that cancels any pending retry. When every retry has
 * been spent, `onExhausted` fires exactly once so the caller can fall the image
 * into a hard-failed state. No-op (passes `baseSrc` straight through) when
 * `enabled` is false.
 */
function useImageRetry(baseSrc, enabled, onExhausted) {
  const $ = _c(18);
  const [attempt, setAttempt] = useState(0);
  const timerRef = useRef(null);
  const exhaustedRef = useRef(false);
  const committedBaseRef = useRef(baseSrc);
  const [prevBase, setPrevBase] = useState(baseSrc);
  if (prevBase !== baseSrc) {
    setPrevBase(baseSrc);
    setAttempt(0);
  }
  let t0;
  let t1;
  if ($[0] !== baseSrc) {
    t0 = () => {
      if (committedBaseRef.current === baseSrc) {
        return;
      }
      committedBaseRef.current = baseSrc;
      exhaustedRef.current = false;
      if (timerRef.current === null) {
        return;
      }
      clearTimeout(timerRef.current);
      timerRef.current = null;
    };
    t1 = [baseSrc];
    $[0] = baseSrc;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useLayoutEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
    t3 = [];
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  const canCacheBust = baseSrc !== null && (baseSrc.startsWith("http://") || baseSrc.startsWith("https://"));
  let t4;
  if ($[5] !== attempt || $[6] !== baseSrc || $[7] !== canCacheBust || $[8] !== enabled) {
    t4 = baseSrc === null || !enabled || attempt === 0 || !canCacheBust ? baseSrc : `${baseSrc}${baseSrc.includes("?") ? "&" : "?"}_retry=${String(attempt)}`;
    $[5] = attempt;
    $[6] = baseSrc;
    $[7] = canCacheBust;
    $[8] = enabled;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const src = t4;
  let t5;
  if ($[10] !== attempt || $[11] !== enabled || $[12] !== onExhausted) {
    t5 = () => {
      if (!enabled) {
        return;
      }
      if (timerRef.current !== null) {
        return;
      }
      if (attempt >= IMAGE_RETRY_DELAYS_MS.length) {
        if (!exhaustedRef.current) {
          exhaustedRef.current = true;
          onExhausted?.();
        }
        return;
      }
      const delay = IMAGE_RETRY_DELAYS_MS[attempt] ?? 3500;
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setAttempt(_temp);
      }, delay);
    };
    $[10] = attempt;
    $[11] = enabled;
    $[12] = onExhausted;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== src || $[16] !== t5) {
    t7 = {
      src,
      handleError: t5,
      handleSuccess: t6
    };
    $[15] = src;
    $[16] = t5;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}
function _temp(a) {
  return a + 1;
}
type Variant = 'thumbnail' | 'hero' | 'avatar';

/**
 * Discriminated union describing the entity an image represents. The
 * façade builds the right manifest internally so callsites never touch
 * `@/lib/pattern-studio`.
 *
 * - `user`: avatars. Initials overlay (when no `avatarUrl`) is the
 *   responsibility of `<UserAvatar>` — the façade only paints the
 *   formatted pattern background.
 * - `cover`: profile cover banners. Synthesised when `coverUrl` is null.
 * - `market`: market / proposal covers. Synthesised per-category when
 *   `coverImageUrl` is null.
 * - `outcome`: outcome thumbnails. Hue is derived by the caller from
 *   `useOutcomeColor` so chart series colour and outcome image stay in
 *   lockstep.
 * - `preview`: live picker / post-crop preview. Renders client-only via
 *   the in-browser blob-URL path; the rendered PNG is owned by the
 *   façade and revoked on unmount.
 */
export type OptimizedImageEntity = {
  kind: 'user';
  userId: string;
  displayName: string | null;
  avatarUrl: string | null;
  /**
   * When `true`, render the avatar through the pattern-studio rasteriser
   * (dithered, brand-graded). When `false` AND `avatarUrl` is non-empty,
   * the raw upload renders directly via the passthrough lane (same path
   * markets/outcomes use). When `false` AND no upload, the rasteriser
   * still runs to generate the deterministic synthetic placeholder.
   *
   * Per-user preference, persisted on `users.avatar_preferences.rasterise`.
   * Defaults to `true` for new users; existing users were backfilled to
   * `false` in migration 0136 so re-uploads keep the look they're used to.
   */
  rasterise: boolean;
  /** When true, blurs the rendered image and overlays a tap-to-reveal CTA until the viewer reveals it for this URL via `useNsfwRevealStore`. */
  nsfw?: boolean;
} | {
  kind: 'cover';
  userId: string;
  coverUrl: string | null;
  /**
   * `dot-studio` (default) — client-rendered colored cover from
   * `heroCoverForUser(userId)`; ignores stale baked IPFS URLs.
   * `passthrough` — raw `coverUrl` (picker preview blob, photo upload).
   */
  coverRender?: 'dot-studio' | 'passthrough';
  /** See `nsfw` on the `user` variant. */
  nsfw?: boolean;
} | {
  kind: 'market';
  pubkey: string;
  category: string;
  coverImageUrl: string | null;
  /** See `nsfw` on the `user` variant. */
  nsfw?: boolean;
} | {
  kind: 'outcome';
  marketPubkey: string;
  outcomeIndex: number;
  hue: OutcomeHueName;
  imageUrl: string | null;
  /** See `nsfw` on the `user` variant. */
  nsfw?: boolean;
} | {
  kind: 'winner';
  mint: string;
  hue: OutcomeHueName;
  imageUrl: string | null;
  /** See `nsfw` on the `user` variant. */
  nsfw?: boolean;
} | {
  kind: 'preview';
  sourceBlobUrl: string;
  paletteRef: PaletteRef;
  seed: number;
};
interface OptimizedImageProps {
  variant: Variant;

  /**
   * Legacy raw-URL API. Kept during the spec-061 migration window; will
   * be removed in phase 7. Mutually exclusive with `entity`. Emits a
   * deprecation warning in dev.
   */
  src?: string;

  /** Preferred entity-driven API. Façade builds the manifest internally. */
  entity?: OptimizedImageEntity;
  alt: string;

  /**
   * Size interpretation depends on variant:
   * - thumbnail: Tailwind size class for container (default "size-10")
   * - hero: ignored (parent provides the container)
   * - avatar: pixel size for width/height (default 40)
   */
  size?: string | number;

  /** Applied to the outer container (thumbnail) or the Image element (avatar/hero). */
  className?: string;

  /** Applied directly to the <Image> element (thumbnail and hero only). */
  imageClassName?: string;

  /** Tailwind rounded class. Defaults per variant: "rounded-control" thumbnail, "rounded-full" avatar, none hero. */
  rounded?: string;

  /** Responsive sizes string (hero only). Default: "(max-width: 768px) 100vw, 640px". */
  sizes?: string;

  /** LCP hint — eager-loads with fetchpriority="high" (hero + thumbnail). */
  priority?: boolean;

  /**
   * Tiny base64 data-URI (e.g. an LQIP from the AI generator). When set, it's
   * rendered via the Next Image `placeholder="blur"` prop so a blurred preview
   * paints INSTANTLY (zero network) while the real `src` loads — and the
   * loading shimmer is suppressed (the blur is the placeholder). thumbnail +
   * hero variants only.
   */
  blurDataURL?: string;

  /**
   * Retry transient fetch failures (e.g. an IPFS gateway 403 while a freshly
   * predicted CID is still propagating its pin) with exponential backoff before
   * giving up. Legacy `src` path only. When every retry is spent, `onError`
   * fires so the caller can render a hard-failed state. Pair with `blurDataURL`
   * so the placeholder stays visible (not the broken-image glyph) while retrying.
   */
  retryOnError?: boolean;

  /** Called when the image fails to load — after retries are exhausted when `retryOnError` is set. */
  onError?: () => void;

  /**
   * Entity path only: fired once when the LAST-RESORT image URL (rasterised
   * pattern / dot-studio blob) fails to load, so no image can be painted.
   * Lets identity wrappers (UserAvatar) swap in their own placeholder — e.g.
   * the initials tile — instead of the neutral PatternFallback disc
   * (MOR-1843).
   */
  onExhausted?: () => void;

  /** Override default quality. Defaults: 75 for thumbnail/avatar, 85 for hero. */
  quality?: number;

  /** Opens the global lightbox preview on click. Default: true. Set false to opt out. */
  preview?: boolean;

  /**
   * Anchor for the `object-cover` crop. Maps to a Tailwind `object-*` class
   * so the cropped image is biased toward the chosen edge instead of the
   * default center. Useful when subjects (e.g. faces) sit near one edge of
   * the source and would otherwise be cropped out by default centering
   * inside a tall/narrow container — e.g. outcome bar images in
   * `MarketRowCard` (spec 062). Default: undefined (Tailwind default = center).
   */
  objectPosition?: 'top' | 'center' | 'bottom' | 'left' | 'right';

  /**
   * Force the pattern-studio rasteriser path even when the entity has an
   * uploaded image URL (which would normally take the passthrough path —
   * see `isPassthroughEntity` and the 2026-05-05 carve-out). The rasteriser
   * dithers the source image through the entity's palette (e.g. the
   * `outcome-hue` palette for outcome entities), producing a brand-colour-
   * graded version of the image. Use for surfaces where the image MUST
   * read in the entity's brand colour family — e.g. outcome bars in
   * `MarketRowCard` (spec 062) where the bar's outcome hue is the primary
   * signal and the image is supplementary identity.
   *
   * Default: `false` (existing passthrough behaviour preserved for every
   * call site that does not opt in).
   */
  forceRaster?: boolean;

  /**
   * When true, the rendered image is heavily blurred and an `<NsfwOverlay>`
   * is layered above it until the viewer taps to reveal. Reveal state is
   * tracked per-URL via `useNsfwRevealStore` so the same image stays
   * revealed across remounts in the same session.
   */
  nsfw?: boolean;

  /**
   * When true AND `nsfw` is also true: the blur is applied as usual, but the
   * `<NsfwOverlay>` reveal button is NOT rendered and the lightbox-preview
   * wrapper is also skipped. Used for surfaces where the blur is purely a
   * UX nudge (e.g. the proposal-creation preview's outcome thumbnails — the
   * creator already knows what they uploaded; the blur is a flag, not a
   * gate). Default: `false`.
   */
  suppressReveal?: boolean;

  /**
   * Controls the load-time reveal animation that plays once the image
   * decodes:
   *
   * - `'default'` (or unset) — the usual scale + blur + opacity tween from
   *   `LOAD_KNOBS.imageFromClasses*` over 600–700 ms. The most visible
   *   "materialising" feel; matches the original behaviour.
   * - `'soft'` — opacity-only fade-in over 300 ms with no scale or blur.
   *   Use when a parent wrapper already owns the dominant reveal and the
   *   default tween would feel heavy on top of it.
   * - `'none'` — the image is rendered directly in its settled state and
   *   no inline transition style is applied at all. Use when the parent
   *   wrapper's own transition is the entire reveal.
   *
   * Distinct from `suppressReveal`, which gates the NSFW reveal button.
   */
  loadReveal?: 'default' | 'soft' | 'none';

  /**
   * Workspace-only debug override — when set, forces the entity-driven
   * pattern rendering to use these params/mosaic instead of the lib's
   * committed DEFAULT_PARAMS. Production callers MUST NOT set this.
   * Used by /workspace/pattern to make MarketRowCards react live to
   * tuner knobs.
   */
  imageOverride?: {
    params: PatternParams;
  } | undefined;
}
const DEFAULT_QUALITY: Record<Variant, number> = {
  thumbnail: 75,
  hero: 85,
  avatar: 75
};
const DEFAULT_ROUNDED: Record<Variant, string> = {
  thumbnail: 'rounded-control',
  hero: '',
  avatar: 'rounded-full'
};
const SSR_DEFAULT_THEME: ManifestThemeName = 'light';

/**
 * Build an object of optional Image props, omitting keys whose values are undefined.
 * This avoids passing `undefined` to optional props, which violates `exactOptionalPropertyTypes`.
 */
function optionalProps(entries: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(entries)) {
    if (value !== undefined) {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Catches render-time errors from Next.js `<Image>` (e.g. unconfigured hostname)
 * and renders a placeholder instead of crashing the page.
 */
interface ImageErrorBoundaryProps {
  fallback: React.ReactNode;
  onCatch?: () => void;
  children: ReactNode;
}
interface ImageErrorBoundaryState {
  hasError: boolean;
}
class ImageErrorBoundary extends Component<ImageErrorBoundaryProps, ImageErrorBoundaryState> {
  static displayName = 'OptimizedImageErrorBoundary';
  constructor(props: ImageErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false
    };
  }
  static getDerivedStateFromError(): ImageErrorBoundaryState {
    return {
      hasError: true
    };
  }
  override componentDidCatch(error: Error): void {
    // Suppress image load errors to warning level (e.g. unconfigured hostname)
    // instead of full error reporting, since these are not critical failures
    sendLog(`Image load failed: ${error.message}`, 'warn', {
      context: 'ImageErrorBoundary'
    });
    this.props.onCatch?.();
  }
  override render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}
function openPreview(src: string, alt: string, wasNsfw: boolean): void {
  useLightboxStore.getState().openLightbox([{
    src,
    alt,
    wasNsfw
  }]);
}

/**
 * Build the appropriate manifest for a non-preview entity. Preview
 * entities are not handled here — they go through the blob-URL path.
 */
function buildEntityManifest(entity: Exclude<OptimizedImageEntity, {
  kind: 'preview';
}>): PatternManifest {
  if (entity.kind === 'user') {
    return manifestForUser({
      userId: entity.userId,
      avatarUrl: entity.avatarUrl
    });
  }
  if (entity.kind === 'cover') {
    return manifestForUserCover({
      userId: entity.userId,
      coverUrl: entity.coverUrl
    });
  }
  if (entity.kind === 'market') {
    return manifestForMarket({
      pubkey: entity.pubkey,
      category: entity.category,
      coverImageUrl: entity.coverImageUrl
    });
  }
  if (entity.kind === 'winner') {
    return manifestForWinner({
      mint: entity.mint,
      hue: entity.hue,
      imageUrl: entity.imageUrl
    });
  }
  return manifestForOutcome({
    marketPubkey: entity.marketPubkey,
    outcomeIndex: entity.outcomeIndex,
    hue: entity.hue,
    imageUrl: entity.imageUrl
  });
}

/**
 * Read the active theme from the document root. SSR has no DOM, so the
 * caller defaults to `'light'` and swaps on hydration via `useEffect`.
 */
function detectClientTheme(): ManifestThemeName {
  if (typeof document === 'undefined') return SSR_DEFAULT_THEME;
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}
function subscribeToClientTheme(onStoreChange: () => void): () => void {
  if (typeof document === 'undefined') return (): void => {};
  const observer = new MutationObserver(onStoreChange);
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class']
  });
  return (): void => {
    observer.disconnect();
  };
}

/**
 * Resolve the URL for an entity-driven render. For `preview` entities
 * this is `null` initially (the blob URL is computed in a `useEffect`
 * because `URL.createObjectURL` is browser-only). For all other kinds
 * this is the deterministic `/api/pattern/<hash>.png` URL.
 *
 * NOTE: server-rendered HTML always emits `theme = 'light'`. The client
 * hydrates to the document theme and re-derives the URL when the root
 * `dark` class toggles. Next.js Image dedupes the fetch; light + dark
 * hashes are CDN-cached independently.
 */
interface EntityUrlDimensions {
  /** Source rasterised PNG width in pixels. Drives `?w=N` on the route URL. */
  readonly width: number;
  /**
   * Source rasterised PNG height in pixels. Drives `?h=N` on the route URL.
   * For avatars pass `width` so the source PNG is square — Next.js Image
   * then proxies/optimises that square source into the avatar slot without
   * `object-cover` cropping a 3:1 default into a circle.
   */
  readonly height: number;
  /**
   * Mosaic-only: override `mosaicParams.cellSize` (cells across the shorter
   * dimension). Drives `?cells=N` on the route URL — avatars use this for
   * per-variant density. Halftone surfaces (market/outcome/cover) ignore it.
   */
  readonly cells?: number;
  /**
   * Crop anchor (0..1) for the rasteriser's "cover" mapping when source
   * and canvas aspects differ. 0 = top/left, 0.5 = centred (default),
   * 1 = bottom/right. Drives `?cax=N` / `?cay=N` on the route URL.
   * Outcome bar images (spec 062) set `cropY = 0` so portrait headshots
   * keep faces inside the rasterised window instead of getting centre-
   * cropped to the chest band.
   */
  readonly cropX?: number;
  readonly cropY?: number;
  /**
   * Target PNG-8 palette size for the route's lossless-ish quantisation pass.
   * Drives `?pal=N`. The rasteriser output is an already-dithered halftone
   * (~250 distinct colours), so quantising to a small palette cuts the PNG
   * several-fold with no visible change. Set only for the low-opacity outcome
   * raster (the MarketRowCard hover overlay, MOR-644); omitted everywhere else
   * so avatars/covers keep their truecolour fidelity.
   */
  readonly paletteColors?: number;
}
function useEntityImageUrl(entity, dimensions, imageOverride) {
  const $ = _c(11);
  const theme = useSyncExternalStore(subscribeToClientTheme, detectClientTheme, _temp2);
  const [blobUrl, setBlobUrl] = useState(null);
  const overrideActive = imageOverride !== undefined && entity.kind !== "preview";
  const overrideParams = imageOverride?.params;
  let t0;
  let t1;
  if ($[0] !== dimensions.height || $[1] !== dimensions.width || $[2] !== entity || $[3] !== overrideActive || $[4] !== overrideParams || $[5] !== theme) {
    t0 = () => {
      if (entity.kind !== "preview" && !overrideActive) {
        return;
      }
      let revoked = false;
      let createdUrl = null;
      const manifest = entity.kind === "preview" ? {
        version: 2,
        source: {
          kind: "url",
          url: entity.sourceBlobUrl
        },
        palette: entity.paletteRef,
        seed: entity.seed
      } : buildEntityManifest(entity);
      renderManifestToBlob(manifest, {
        width: dimensions.width,
        height: dimensions.height,
        theme,
        ...(overrideActive && overrideParams !== undefined ? {
          debugOverride: {
            params: overrideParams
          }
        } : {})
      }).then(blob => {
        const url = URL.createObjectURL(blob);
        if (revoked) {
          URL.revokeObjectURL(url);
          return;
        }
        createdUrl = url;
        setBlobUrl(url);
      }).catch(_temp3);
      return () => {
        revoked = true;
        if (createdUrl !== null) {
          URL.revokeObjectURL(createdUrl);
        }
        setBlobUrl(null);
      };
    };
    t1 = [entity, dimensions.width, dimensions.height, overrideActive, overrideParams, theme];
    $[0] = dimensions.height;
    $[1] = dimensions.width;
    $[2] = entity;
    $[3] = overrideActive;
    $[4] = overrideParams;
    $[5] = theme;
    $[6] = t0;
    $[7] = t1;
  } else {
    t0 = $[6];
    t1 = $[7];
  }
  useEffect(t0, t1);
  if (entity.kind === "preview" || overrideActive) {
    return blobUrl;
  }
  let t2;
  if ($[8] !== entity || $[9] !== theme) {
    const manifest_0 = buildEntityManifest(entity);
    t2 = encodeManifestHash(manifest_0, theme);
    $[8] = entity;
    $[9] = theme;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const hash = t2;
  const cellsSuffix = dimensions.cells !== undefined ? `&cells=${String(dimensions.cells)}` : "";
  const caxSuffix = dimensions.cropX !== undefined ? `&cax=${String(dimensions.cropX)}` : "";
  const caySuffix = dimensions.cropY !== undefined ? `&cay=${String(dimensions.cropY)}` : "";
  const palSuffix = dimensions.paletteColors !== undefined ? `&pal=${String(dimensions.paletteColors)}` : "";
  return `/api/pattern/${hash}.png?w=${String(dimensions.width)}&h=${String(dimensions.height)}${cellsSuffix}${caxSuffix}${caySuffix}${palSuffix}`;
}

/** Live Dot Studio hero — same recipe as `heroCoverForUser` + picker export. */
function _temp3(err) {
  sendLog(`pattern-studio: preview render failed: ${err instanceof Error ? err.message : "unknown"}`, "warn", {
    context: "OptimizedImage.preview"
  });
}
function _temp2() {
  return SSR_DEFAULT_THEME;
}
function useDotStudioCoverBlobUrl(userId, coverUrl, width, height, enabled) {
  const $ = _c(7);
  const [blobUrl, setBlobUrl] = useState(null);
  let t0;
  let t1;
  if ($[0] !== coverUrl || $[1] !== enabled || $[2] !== height || $[3] !== userId || $[4] !== width) {
    t0 = () => {
      if (!enabled || userId.length === 0) {
        return;
      }
      let revoked = false;
      let createdUrl = null;
      const pattern = resolveHeroCoverPattern(userId, coverUrl);
      generateDotStudioCoverPng(pattern.variant, pattern.seedText, {
        width,
        height,
        mimeType: "image/png",
        overrides: pattern.overrides
      }).then(blob => {
        if (revoked) {
          return;
        }
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      }).catch(_temp4);
      return () => {
        revoked = true;
        if (createdUrl !== null) {
          URL.revokeObjectURL(createdUrl);
        }
        setBlobUrl(null);
      };
    };
    t1 = [userId, coverUrl, width, height, enabled];
    $[0] = coverUrl;
    $[1] = enabled;
    $[2] = height;
    $[3] = userId;
    $[4] = width;
    $[5] = t0;
    $[6] = t1;
  } else {
    t0 = $[5];
    t1 = $[6];
  }
  useEffect(t0, t1);
  if (!enabled || userId.length === 0) {
    return null;
  }
  return blobUrl;
}

/**
 * Skeleton fallback rendered while a `preview` blob URL is computing,
 * or as the boundary fallback when the URL fetch fails. Palette-tinted
 * via the same neutral surface tokens used elsewhere.
 */
function _temp4(err) {
  sendLog(`pattern-studio: dot-studio cover render failed: ${err instanceof Error ? err.message : "unknown"}`, "warn", {
    context: "OptimizedImage.dotStudioCover"
  });
}
function PatternFallback(props) {
  const $ = _c(13);
  if (props.fill === true) {
    let t0;
    if ($[0] !== props.className || $[1] !== props.rounded) {
      t0 = cn("absolute inset-0 bg-image-halftone-surface", props.rounded, props.className);
      $[0] = props.className;
      $[1] = props.rounded;
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    let t1;
    if ($[3] !== t0) {
      t1 = <div className={t0} aria-hidden="true" />;
      $[3] = t0;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  let t0;
  if ($[5] !== props.pixelSize) {
    t0 = props.pixelSize !== undefined ? {
      width: `${String(props.pixelSize)}px`,
      height: `${String(props.pixelSize)}px`
    } : undefined;
    $[5] = props.pixelSize;
    $[6] = t0;
  } else {
    t0 = $[6];
  }
  let t1;
  if ($[7] !== props.className || $[8] !== props.rounded) {
    t1 = cn("block shrink-0 bg-image-halftone-surface", props.rounded, props.className);
    $[7] = props.className;
    $[8] = props.rounded;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== t0 || $[11] !== t1) {
    t2 = <div style={t0} className={t1} aria-hidden="true" />;
    $[10] = t0;
    $[11] = t1;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  return t2;
}
interface VariantRenderArgs {
  src: string | null;
  alt: string;
  resolvedQuality: number;
  resolvedRounded: string;
  className?: string;
  imageClassName?: string;
  objectPositionClass?: string;
  size?: string | number;
  sizes?: string;
  priority?: boolean;
  blurDataURL?: string;
  onError?: () => void;
  preview?: boolean;
  previewLabel: string;
  /**
   * When true, the underlying image is blurred and an `<NsfwOverlay>` is
   * layered on top until the viewer reveals via the reveal store. Each
   * variant render is responsible for honouring this flag; preview
   * entities (creator looking at their own pending upload) never set it.
   */
  nsfw: boolean;
  /** True once the viewer has revealed the URL via the reveal store. */
  revealed: boolean;
  /** Stable callback that flips the reveal state for `args.src`. */
  onReveal: () => void;
  /**
   * When true AND `nsfw` is also true, the blur is rendered but the
   * `<NsfwOverlay>` (reveal button) is suppressed and the lightbox-preview
   * wrapper is skipped. See `OptimizedImageProps.suppressReveal`.
   */
  suppressReveal: boolean;
  /**
   * Variant of the load-time reveal animation. See
   * `OptimizedImageProps.loadReveal` for the three states and their use
   * cases. Resolved to a concrete value at the public-API boundary.
   */
  loadReveal: 'default' | 'soft' | 'none';
  loaded: boolean;
  /**
   * True when the LAST-RESORT image URL (rasterised pattern / dot-studio
   * blob) failed to load, so there is nothing left to fetch. The avatar
   * variant renders the client-side <PatternFallback> placeholder instead
   * of a stuck shimmer (MOR-1843). Entity path only — absent on the legacy
   * `src` path.
   */
  hardFailed?: boolean;
  /**
   * True when the image was served from browser cache and there's no fetch
   * to wait on. Variants treat this the same as `loaded || reducedMotion`
   * for from/to state, AND skip the inline-style transition so the cache
   * hit doesn't briefly animate. NOTE: the underlying ref is intentionally
   * NOT on this interface — the React Compiler refuses to track refs that
   * flow through opaque arg objects, so the ref is passed as a separate
   * positional parameter to each variant render below.
   */
  instant: boolean;
  handleLoad: () => void;
  reducedMotion: boolean;
  /**
   * False when the image's outer wrapper is not yet near the viewport AND
   * `priority` is not set. The variant render functions translate this
   * into `src=null` upstream so the existing `<PatternFallback>` skeleton
   * renders without firing a fetch. Once the IntersectionObserver flips
   * this true, it stays true (sticky) so scroll-out doesn't re-gate.
   * Bypassed for `kind: 'preview'` (creator looking at own upload).
   */
  shouldRender: boolean;
}
function renderThumbnail(args: VariantRenderArgs, imgRef: RefObject<HTMLImageElement | null>, containerRef: RefObject<HTMLDivElement | null>): React.ReactNode {
  const sizeClass: string = typeof args.size === 'string' ? args.size : 'size-10';
  // Bypass Next.js Image's optimisation pipeline for the rasteriser route,
  // blob URLs and data URLs — they're already optimised and adding the
  // optimiser pass would double-encode / fight `images.localPatterns`.
  const isUnoptimisedSrc = typeof args.src === 'string' && (args.src.startsWith('/api/pattern/') || args.src.startsWith('/api/dev-internal/') || args.src.startsWith('blob:') || args.src.startsWith('data:'));

  // Settled = no entrance animation (image is ready or shouldn't animate).
  // `loadReveal: 'none'` forces settled so the image renders in its final
  // state immediately with no inline transition style.
  // When a `blurDataURL` (LQIP) is supplied we paint it as a dedicated layer
  // BEHIND the image (see `blurLayer` below) and reveal the real image with the
  // soft opacity fade on load. Decoupling the blur from the image element — vs.
  // Next's `placeholder="blur"`, which lives ON the image element — means a slow or
  // failing fetch (e.g. an IPFS 403 while a predicted CID is still propagating)
  // shows the blur, not the broken-image glyph, and a retry can swap the real
  // bytes in underneath. The image stays `opacity-0` until it actually loads.
  const hasBlur = args.blurDataURL !== undefined;
  const blurRevealed = args.loaded || args.instant;
  const settled = args.loaded || args.instant || args.reducedMotion || args.loadReveal === 'none';
  // Skip the inline transition style on cache-hit / reduced-motion / when
  // the caller opted out, so the swap from the from-state DOM commit to the
  // settled commit happens without any interpolation. Without this, a cache
  // hit could briefly tween.
  const skipTransition = args.instant || args.reducedMotion || args.loadReveal === 'none';
  // Resolved inline transition style — falls through to `IMAGE_LOAD_TRANSITION_STYLE_SOFT`
  // (opacity-only over 300 ms) when the caller opted in to the soft variant.
  const transitionStyle: CSSProperties = args.loadReveal === 'soft' ? IMAGE_LOAD_TRANSITION_STYLE_SOFT : IMAGE_LOAD_TRANSITION_STYLE;
  // Soft mode pares the from-state down to opacity-only (no scale, no blur)
  // so the 300 ms fade reads as a light dissolve rather than a "materialising"
  // tween.
  const softFromClasses = 'opacity-0';
  const nsfwBlurred = args.nsfw && !args.revealed;
  const inner: React.ReactNode = args.src === null ? <PatternFallback fill rounded="" /> : <>
        {hasBlur ? null : <ShimmerStyle />}
        {hasBlur ? !blurRevealed ? <>
              <div aria-hidden="true" className="absolute inset-0 scale-110 bg-cover bg-center blur-md"
      // ARBITRARY: LQIP is a runtime base64 data-URI, not expressible as a Tailwind class
      style={{
        backgroundImage: `url("${args.blurDataURL ?? ''}")`
      }} />
              <div aria-hidden="true" className="absolute inset-0 flex items-center justify-center">
                <Icon icon={Loading03Icon} className="size-4 animate-spin text-foreground-primary drop-shadow-sm motion-reduce:animate-none" />
              </div>
            </> : null : <div aria-hidden="true" className={cn('morfi-image-shimmer absolute inset-0', (args.loaded || args.instant) && 'opacity-0')} {...skipTransition ? {} : {
      style: SKELETON_FADE_OUT_STYLE
    }} />}
        <ImageErrorBoundary fallback={<PatternFallback fill rounded="" />} {...args.onError !== undefined ? {
      onCatch: args.onError
    } : {}}>
          <Image ref={imgRef} src={args.src} alt={args.alt} fill className={cn('object-cover', args.objectPositionClass, args.imageClassName, hasBlur ? blurRevealed ? 'scale-100 opacity-100 blur-0' : 'opacity-0' : settled ? 'scale-100 opacity-100 blur-0' : args.loadReveal === 'soft' ? softFromClasses : LOAD_KNOBS.imageFromClassesThumbnail, nsfwBlurred && 'blur-xl scale-110 brightness-50')} sizes="96px" unoptimized={isUnoptimisedSrc} onLoad={args.handleLoad} {...hasBlur ? args.reducedMotion ? {} : {
        style: IMAGE_LOAD_TRANSITION_STYLE_SOFT
      } : skipTransition ? {} : {
        style: transitionStyle
      }} {...optionalProps({
        quality: isUnoptimisedSrc ? undefined : args.resolvedQuality,
        // LCP: an above-the-fold thumbnail loads eagerly (`priority` drops
        // lazy) with an explicit fetchpriority="high" — Next 16 does NOT
        // derive fetchpriority from `priority`. Mirrors renderHero; a falsy
        // priority omits both, keeping the default lazy fetch below the
        // fold. See MOR-566.
        priority: args.priority,
        fetchPriority: args.priority === true ? 'high' : undefined,
        onError: args.onError
      })} />
        </ImageErrorBoundary>
        {nsfwBlurred && !args.suppressReveal ? <NsfwOverlay variant="thumb" onReveal={args.onReveal} /> : null}
      </>;
  const container: React.ReactNode = <div ref={containerRef} className={cn('relative shrink-0 overflow-hidden bg-image-halftone-surface', sizeClass, args.resolvedRounded, args.preview !== false ? undefined : args.className)}>
      {inner}
    </div>;
  if (args.preview !== false && args.src !== null && !nsfwBlurred && !args.suppressReveal) {
    return <button type="button" onClick={(): void => {
      openPreview(args.src as string, args.alt, args.nsfw);
    }} className={cn('cursor-pointer transition-opacity hover:opacity-90', args.className)} aria-label={args.previewLabel}>
        {container}
      </button>;
  }
  return container;
}
function renderAvatar(args: VariantRenderArgs, imgRef: RefObject<HTMLImageElement | null>, containerRef: RefObject<HTMLDivElement | null>): React.ReactNode {
  const pixelSize: number = typeof args.size === 'number' ? args.size : 40;
  const fallbackClassName: string | undefined = args.preview !== false ? undefined : args.className;
  const rawWrapperClassName: string | undefined = args.preview !== false ? undefined : args.className;
  const avatarFillsParent = rawWrapperClassName !== undefined && /\babsolute\b/.test(rawWrapperClassName) && /\binset-0\b/.test(rawWrapperClassName);
  const wrapperClassName = rawWrapperClassName;
  const wrapperSizeStyle: CSSProperties | undefined = avatarFillsParent ? undefined : {
    width: `${String(pixelSize)}px`,
    height: `${String(pixelSize)}px`
  };
  if (args.hardFailed === true) {
    // Every URL in the chain (passthrough upload → rasterised pattern) failed
    // to load. Paint the client-side placeholder instead of a stuck shimmer
    // (MOR-1843). Same ref-bearing wrapper shape as the branches below.
    return <div ref={containerRef} className={cn(avatarFillsParent ? 'min-h-0 min-w-0 overflow-hidden' : 'relative inline-block shrink-0 overflow-hidden', args.resolvedRounded, fallbackClassName)} style={wrapperSizeStyle}>
        <PatternFallback fill rounded={args.resolvedRounded} />
      </div>;
  }
  if (args.src === null) {
    // Wrap the shared Skeleton in a sized box so `containerRef` has a stable
    // ref-attachable element across both src=null and src=URL branches —
    // the IntersectionObserver needs the same observation target as the
    // image-rendering branch's outer div below.
    return <div ref={containerRef} className={cn(avatarFillsParent ? 'min-h-0 min-w-0 overflow-hidden' : 'relative inline-block shrink-0 overflow-hidden', 'bg-background-tertiary', args.resolvedRounded, fallbackClassName)} style={wrapperSizeStyle}>
        <ShimmerStyle />
        <Skeleton className={cn('absolute inset-0', args.resolvedRounded, !args.shouldRender && 'morfi-image-skeleton-offscreen')} />
      </div>;
  }
  const isDataUrl: boolean = args.src.startsWith('data:');
  const isPattern: boolean = args.src.startsWith('/api/pattern/');
  const isDevInternal: boolean = args.src.startsWith('/api/dev-internal/');
  const isBlob: boolean = args.src.startsWith('blob:');
  // IPFS URLs (images.morfi.markets/ipfs/**) go THROUGH Next.js Image
  // optimization — they're allowlisted in next.config.ts remotePatterns and
  // the thumbnail variant has always optimized them. The avatar variant
  // previously short-circuited via `isIpfs` here, which made it serve the
  // full-resolution upload (typically 500×500) regardless of `sizes={pixelSize}px`
  // — surfaced in the 2026-05-24 /demo-mode trace as ~290 kB of wasted
  // image bytes across 7 small avatars/icons. Removing the carve-out lets
  // Next pick a bucket from `imageSizes: [48, 96, 128, 256]` per the
  // `sizes` hint emitted below.
  const unoptimized = isDataUrl || isPattern || isDevInternal || isBlob;
  const nsfwBlurred = args.nsfw && !args.revealed;
  const image: React.ReactNode = <div ref={containerRef} className={cn(avatarFillsParent ? 'min-h-0 min-w-0 overflow-hidden' : 'relative inline-block shrink-0 overflow-hidden', args.resolvedRounded, wrapperClassName)} style={wrapperSizeStyle}>
      <ShimmerStyle />
      <div aria-hidden="true" className={cn('morfi-image-shimmer morfi-image-shimmer-avatar absolute inset-0', (args.loaded || args.instant) && 'opacity-0')} {...args.reducedMotion || args.instant ? {} : {
      style: SKELETON_FADE_OUT_STYLE
    }} />
      <ImageErrorBoundary fallback={<PatternFallback pixelSize={pixelSize} rounded={args.resolvedRounded} fill={avatarFillsParent} {...fallbackClassName !== undefined ? {
      className: fallbackClassName
    } : {}} />} {...args.onError !== undefined ? {
      onCatch: args.onError
    } : {}}>
        <Image ref={imgRef} src={args.src} alt={args.alt} fill sizes={`${String(pixelSize)}px`} className={cn('object-cover', args.objectPositionClass, args.resolvedRounded, args.loaded || args.instant || args.reducedMotion || args.loadReveal === 'none' ? 'scale-100 opacity-100 blur-0' : args.loadReveal === 'soft' ? 'opacity-0' : LOAD_KNOBS.imageFromClassesAvatar,
      // Avatar surface is small (~24–96 px) — the hero's `blur-xl`
      // would smear the entire rasterised pattern into a single flat
      // colour, hiding the "image rendered, just covered" signal.
      // `blur-sm` keeps enough texture visible that the user can see
      // there's an image underneath, while still obscuring detail.
      nsfwBlurred && 'blur-sm scale-110')} {...args.reducedMotion || args.instant || args.loadReveal === 'none' ? {} : {
        style: args.loadReveal === 'soft' ? IMAGE_LOAD_TRANSITION_STYLE_SOFT : IMAGE_LOAD_TRANSITION_STYLE
      }} unoptimized={unoptimized} onLoad={args.handleLoad} {...optionalProps({
        quality: unoptimized ? undefined : args.resolvedQuality,
        onError: args.onError
      })} />
      </ImageErrorBoundary>
      {nsfwBlurred && !args.suppressReveal ? <NsfwOverlay variant="avatar" onReveal={args.onReveal} /> : null}
    </div>;
  if (args.preview !== false && !nsfwBlurred && !args.suppressReveal) {
    return <button type="button" onClick={(): void => {
      openPreview(args.src as string, args.alt, args.nsfw);
    }} className={cn('cursor-pointer transition-opacity hover:opacity-90', args.className)} aria-label={args.previewLabel}>
        {image}
      </button>;
  }
  return image;
}
function renderHero(args: VariantRenderArgs, imgRef: RefObject<HTMLImageElement | null>, containerRef: RefObject<HTMLDivElement | null>): React.ReactNode {
  const isUnoptimisedSrc = typeof args.src === 'string' && (args.src.startsWith('/api/pattern/') || args.src.startsWith('/api/dev-internal/') || args.src.startsWith('blob:') || args.src.startsWith('data:') ||
  // Generative Dot Studio covers are fine-pattern PNGs — AVIF/WebP
  // recompression and bucket downscaling can shift apparent dot scale vs
  // the picker preview (blob, unoptimized). Skip Next's optimizer for IPFS
  // and only cap full-resolution AI originals at the gateway (MOR-2292);
  // scale-down leaves the 1024² pattern PNGs byte-identical.
  args.src.includes('images.morfi.markets/ipfs/'));
  const nsfwBlurred = args.nsfw && !args.revealed;
  // When a `blurDataURL` (LQIP) is supplied we paint it as a dedicated layer
  // behind the image and reveal the real image with the soft opacity fade on
  // load — see the renderThumbnail note. Decoupling the blur from the image element
  // means a slow/failing fetch shows the blur, not the broken-image glyph.
  const hasBlur = args.blurDataURL !== undefined;
  const blurRevealed = args.loaded || args.instant;
  const heroImage: React.ReactNode = args.src === null ? <PatternFallback fill rounded={args.resolvedRounded} /> : <>
        {hasBlur ? null : <ShimmerStyle />}
        {hasBlur ? !blurRevealed ? <>
              <div aria-hidden="true" className={cn('absolute inset-0 scale-110 bg-cover bg-center blur-md', args.resolvedRounded)}
      // ARBITRARY: LQIP is a runtime base64 data-URI, not expressible as a Tailwind class
      style={{
        backgroundImage: `url("${args.blurDataURL ?? ''}")`
      }} />
              <div aria-hidden="true" className="absolute inset-0 flex items-center justify-center">
                <Icon icon={Loading03Icon} className="size-6 animate-spin text-foreground-primary drop-shadow-sm motion-reduce:animate-none" />
              </div>
            </> : null : <div aria-hidden="true" className={cn('morfi-image-shimmer absolute inset-0', args.resolvedRounded, (args.loaded || args.instant) && 'opacity-0')} {...args.reducedMotion || args.instant ? {} : {
      style: SKELETON_FADE_OUT_STYLE
    }} />}
        <ImageErrorBoundary fallback={<PatternFallback fill rounded={args.resolvedRounded} />} {...args.onError !== undefined ? {
      onCatch: args.onError
    } : {}}>
          <Image ref={imgRef} src={ipfsImageUrl(args.src, {
        width: HERO_MAX_WIDTH_PX
      })} alt={args.alt} fill className={cn('object-cover', args.objectPositionClass, args.imageClassName, hasBlur ? blurRevealed ? 'scale-100 opacity-100 blur-0' : 'opacity-0' : args.loaded || args.instant || args.reducedMotion || args.loadReveal === 'none' ? 'scale-100 opacity-100 blur-0' : args.loadReveal === 'soft' ? 'opacity-0' : LOAD_KNOBS.imageFromClassesHero, nsfwBlurred && 'blur-xl scale-110 brightness-50')} sizes={args.sizes ?? '(max-width: 768px) 100vw, 640px'} {...hasBlur ? args.reducedMotion ? {} : {
        style: IMAGE_LOAD_TRANSITION_STYLE_SOFT
      } : args.reducedMotion || args.instant || args.loadReveal === 'none' ? {} : {
        style: args.loadReveal === 'soft' ? IMAGE_LOAD_TRANSITION_STYLE_SOFT : IMAGE_LOAD_TRANSITION_STYLE
      }} unoptimized={isUnoptimisedSrc} onLoad={args.handleLoad} {...optionalProps({
        quality: isUnoptimisedSrc ? undefined : args.resolvedQuality,
        // `priority` drops lazy + emits a preload <link>; Next 16 does NOT
        // derive fetchpriority from it, so set it explicitly for the LCP
        // boost an above-the-fold caller asked for. See MOR-566.
        priority: args.priority,
        fetchPriority: args.priority === true ? 'high' : undefined,
        onError: args.onError
      })} />
        </ImageErrorBoundary>
        {nsfwBlurred && !args.suppressReveal ? <NsfwOverlay variant="hero" onReveal={args.onReveal} /> : null}
      </>;

  // Wrap the variant render in a stable absolute-inset wrapper that holds
  // the IntersectionObserver target. Hero variant relies on the caller for a
  // positioned ancestor (e.g. a relative aspect-ratio div in
  // HeroBanner / market-row-card). The wrapper is `absolute inset-0` so it
  // is coincident with the caller's box; children (next/image with fill,
  // shimmer overlay, NsfwOverlay) resolve their `inset-0` / `fill` against
  // the wrapper, which is the same rectangle as before. Visually identical.
  const wrapped: React.ReactNode = <div ref={containerRef} className="absolute inset-0">
      {heroImage}
    </div>;
  if (args.preview !== false && args.src !== null && !nsfwBlurred && !args.suppressReveal) {
    return <button type="button" onClick={(): void => {
      openPreview(args.src as string, args.alt, args.nsfw);
    }} className="contents cursor-pointer" aria-label={args.previewLabel}>
        {wrapped}
      </button>;
  }
  return wrapped;
}

/**
 * Entity classes that bypass the pattern-studio dither at the user's
 * direction. Market thumbnails and outcome indicators render the source
 * image directly (2026-05-05 carve-out — uploaded covers come through
 * as regular optimised images, missing covers fall back to a
 * deterministic palette-tinted skeleton, no dither, no synthetic
 * placeholder pattern).
 *
 * User avatars opt in to the same lane via `entity.rasterise === false`
 * AND a non-empty `avatarUrl` — the per-user preference persisted on
 * `users.avatar_preferences.rasterise`. `rasterise === true` keeps the
 * dithered render; the synthetic placeholder for users without an upload
 * still goes through the rasteriser regardless.
 *
 * Profile covers (`kind: 'cover'`) use the passthrough lane only when
 * `coverRender === 'passthrough'` and a `coverUrl` is set (photo uploads,
 * picker preview blobs). Default `coverRender` is `dot-studio`: the hero
 * is painted client-side from `heroCoverForUser(userId)` so existing
 * baked IPFS generative URLs are not required.
 *
 * `<OptimizedImage>` remains the single image façade and the no-raw-image
 * rule is preserved — these entities just take the URL-passthrough path
 * inside the same component instead of the rasteriser path.
 */
function isPassthroughEntity(entity: OptimizedImageEntity): entity is Extract<OptimizedImageEntity, {
  kind: 'market' | 'outcome' | 'winner' | 'user' | 'cover';
}> {
  if (entity.kind === 'market' || entity.kind === 'outcome' || entity.kind === 'winner') return true;
  if (entity.kind === 'user') {
    return entity.rasterise === false && typeof entity.avatarUrl === 'string' && entity.avatarUrl.length > 0;
  }
  if (entity.kind === 'cover') {
    return entity.coverRender === 'passthrough' && typeof entity.coverUrl === 'string' && entity.coverUrl.length > 0;
  }
  return false;
}

/**
 * Resolve a passthrough entity to its raw URL (uploaded image) or `null`
 * (no upload — the variant render falls back to a palette-tinted skeleton).
 */
function passthroughSrc(entity: Extract<OptimizedImageEntity, {
  kind: 'market' | 'outcome' | 'winner' | 'user' | 'cover';
}>): string | null {
  if (entity.kind === 'user') {
    return typeof entity.avatarUrl === 'string' && entity.avatarUrl.length > 0 ? entity.avatarUrl : null;
  }
  if (entity.kind === 'cover') {
    return typeof entity.coverUrl === 'string' && entity.coverUrl.length > 0 ? entity.coverUrl : null;
  }
  if (entity.kind === 'market') {
    return typeof entity.coverImageUrl === 'string' && entity.coverImageUrl.length > 0 ? entity.coverImageUrl : null;
  }
  return typeof entity.imageUrl === 'string' && entity.imageUrl.length > 0 ? entity.imageUrl : null;
}

/**
 * Inner component for entity-driven renders. The `useEffect`-driven
 * URL resolution lives here so the legacy `src` path stays free of
 * hooks and renders synchronously.
 */
function EntityDriven(props) {
  const $ = _c(35);
  let t0;
  bb0: {
    if (props.variant === "avatar") {
      const px = typeof props.args.size === "number" ? props.args.size : 96;
      const sourcePx = Math.min(2400, Math.max(512, px * 4));
      let t1;
      if ($[0] !== sourcePx) {
        t1 = {
          width: sourcePx,
          height: sourcePx,
          cells: 80
        };
        $[0] = sourcePx;
        $[1] = t1;
      } else {
        t1 = $[1];
      }
      t0 = t1;
      break bb0;
    }
    if (props.variant === "thumbnail") {
      let t1;
      if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = {
          width: 800,
          height: 800,
          cells: 80
        };
        $[2] = t1;
      } else {
        t1 = $[2];
      }
      t0 = t1;
      break bb0;
    }
    if (props.entity.kind === "outcome" || props.entity.kind === "winner") {
      let t1;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = {
          width: 512,
          height: 512,
          cells: 80,
          paletteColors: 128
        };
        $[3] = t1;
      } else {
        t1 = $[3];
      }
      t0 = t1;
      break bb0;
    }
    let t1;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = {
        width: 1200,
        height: 400,
        cells: 96
      };
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    t0 = t1;
  }
  const dims = t0;
  const rasterisedUrl = useEntityImageUrl(props.entity, dims, props.imageOverride);
  const dotStudioCoverActive = props.variant === "hero" && props.entity.kind === "cover" && props.entity.coverRender !== "passthrough";
  const dotStudioBlobUrl = useDotStudioCoverBlobUrl(props.entity.kind === "cover" ? props.entity.userId : "", props.entity.kind === "cover" ? props.entity.coverUrl : null, dims.width, dims.height, dotStudioCoverActive);
  let t1;
  if ($[5] !== props.entity) {
    t1 = isPassthroughEntity(props.entity) ? toCustomGatewayOrNull(passthroughSrc(props.entity)) : null;
    $[5] = props.entity;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const uploadedSrc = t1;
  const [brokenSrc, setBrokenSrc] = useState(null);
  const passthroughBroken = brokenSrc !== null && brokenSrc === uploadedSrc;
  const usePassthrough = props.imageOverride === undefined && !props.forceRaster && uploadedSrc !== null && !passthroughBroken;
  const computedSrc = usePassthrough ? uploadedSrc : dotStudioCoverActive ? dotStudioBlobUrl : rasterisedUrl;
  const src = props.args.shouldRender ? computedSrc : null;
  const [brokenFallbackSrc, setBrokenFallbackSrc] = useState(null);
  const hardFailed = brokenFallbackSrc !== null && brokenFallbackSrc === computedSrc;
  const baseOnError = props.args.onError;
  let t2;
  if ($[7] !== baseOnError || $[8] !== brokenFallbackSrc || $[9] !== computedSrc || $[10] !== props || $[11] !== uploadedSrc || $[12] !== usePassthrough) {
    t2 = usePassthrough && uploadedSrc !== null ? () => {
      setBrokenSrc(uploadedSrc);
      baseOnError?.();
    } : () => {
      if (computedSrc !== null && brokenFallbackSrc !== computedSrc) {
        sendLog("OptimizedImage: fallback image URL failed to load", "warn", {
          context: "OptimizedImage"
        });
        setBrokenFallbackSrc(computedSrc);
        props.onExhausted?.();
      }
      baseOnError?.();
    };
    $[7] = baseOnError;
    $[8] = brokenFallbackSrc;
    $[9] = computedSrc;
    $[10] = props;
    $[11] = uploadedSrc;
    $[12] = usePassthrough;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const onError = t2;
  const {
    loaded,
    instant,
    imgRef,
    handleLoad
  } = useImageLoadState(src);
  const reducedMotion = useReducedMotion() === true;
  const nsfw = props.entity.kind !== "preview" ? props.entity.nsfw === true : false;
  let t3;
  if ($[14] !== src) {
    t3 = s => src !== null ? s.isRevealed(src) : true;
    $[14] = src;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const revealed = useNsfwRevealStore(t3);
  let t4;
  if ($[16] !== src) {
    t4 = function handleReveal() {
      if (src !== null) {
        useNsfwRevealStore.getState().reveal(src);
      }
    };
    $[16] = src;
    $[17] = t4;
  } else {
    t4 = $[17];
  }
  const handleReveal = t4;
  const t5 = props.args;
  const t6 = props.suppressReveal;
  let t7;
  let t8;
  if ($[18] !== handleLoad || $[19] !== handleReveal || $[20] !== hardFailed || $[21] !== imgRef || $[22] !== instant || $[23] !== loaded || $[24] !== nsfw || $[25] !== onError || $[26] !== props.args || $[27] !== props.containerRef || $[28] !== props.suppressReveal || $[29] !== props.variant || $[30] !== reducedMotion || $[31] !== revealed || $[32] !== src) {
    t8 = Symbol.for("react.early_return_sentinel");
    bb1: {
      const args = {
        ...t5,
        src,
        loaded,
        instant,
        handleLoad,
        reducedMotion,
        nsfw,
        revealed,
        onReveal: handleReveal,
        suppressReveal: t6,
        hardFailed,
        ...(onError !== undefined ? {
          onError
        } : {})
      };
      if (props.variant === "thumbnail") {
        t8 = renderThumbnail(args, imgRef, props.containerRef);
        break bb1;
      }
      if (props.variant === "avatar") {
        t8 = renderAvatar(args, imgRef, props.containerRef);
        break bb1;
      }
      t7 = renderHero(args, imgRef, props.containerRef);
    }
    $[18] = handleLoad;
    $[19] = handleReveal;
    $[20] = hardFailed;
    $[21] = imgRef;
    $[22] = instant;
    $[23] = loaded;
    $[24] = nsfw;
    $[25] = onError;
    $[26] = props.args;
    $[27] = props.containerRef;
    $[28] = props.suppressReveal;
    $[29] = props.variant;
    $[30] = reducedMotion;
    $[31] = revealed;
    $[32] = src;
    $[33] = t7;
    $[34] = t8;
  } else {
    t7 = $[33];
    t8 = $[34];
  }
  if (t8 !== Symbol.for("react.early_return_sentinel")) {
    return t8;
  }
  return t7;
}

/**
 * Centralized image component wrapping Next.js `<Image>` and the single
 * frontend façade for `@/lib/pattern-studio`. Three variants cover
 * every image surface in the app:
 *
 * - **thumbnail** — fixed-size container with fill image (cards, rows)
 * - **hero** — responsive fill image with blur placeholder (banners)
 * - **avatar** — fixed width/height circle (user identity)
 *
 * Two creation paths during the spec-061 migration window:
 *
 * - `entity={...}` (preferred): builds the manifest internally and
 *   renders either `/api/pattern/<hash>.png` (server-rasterised) or a
 *   client-only blob URL (`kind: 'preview'`).
 * - `src={...}` (legacy): renders the raw URL through Next.js Image.
 *   Emits a one-time dev-mode deprecation warning. Will be removed in
 *   phase 7 once `FQ-IMG-NO-RAW-001` flips to error.
 *
 * Right-click / save-image yields the formatted PNG (entity path) or
 * the raw upload (legacy path). The lightbox shows whatever URL is
 * being rendered — for `entity`-driven images this is always the
 * formatted artefact, never the raw upload, satisfying the
 * "no raw image rendered to the user" rule from `images-plan.md`.
 *
 * Avatar initials overlay is the responsibility of `<UserAvatar>`. The
 * façade only paints the formatted pattern background — initials live
 * in the wrapper layer.
 *
 * NOTE on theme (v1): server-rendered HTML for entity-driven images
 * uses `theme = 'light'` by default, then re-derives the active theme
 * client-side after hydration. On dark-mode pages this produces a
 * one-frame URL swap — accepted as an explicit v1 limitation per
 * `images-plan.md` §C5. Both light and dark URLs are CDN-cached.
 */
export function OptimizedImage(props) {
  const $ = _c(45);
  const tImage = useTranslations("image");
  const resolvedQuality = props.quality ?? DEFAULT_QUALITY[props.variant];
  const resolvedRounded = props.rounded ?? DEFAULT_ROUNDED[props.variant];
  const legacyLoadState = useImageLoadState(props.src ?? null);
  const reducedMotion = useReducedMotion() === true;
  const legacySrcKey = props.src ?? "";
  let t0;
  if ($[0] !== legacySrcKey) {
    t0 = s => legacySrcKey !== "" ? s.isRevealed(legacySrcKey) : true;
    $[0] = legacySrcKey;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const legacyRevealed = useNsfwRevealStore(t0);
  let t1;
  if ($[2] !== legacySrcKey) {
    t1 = function handleLegacyReveal() {
      if (legacySrcKey !== "") {
        useNsfwRevealStore.getState().reveal(legacySrcKey);
      }
    };
    $[2] = legacySrcKey;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleLegacyReveal = t1;
  const t2 = props.priority === true;
  const t3 = props.entity?.kind === "preview";
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = {
      priority: t2,
      preview: t3
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const gate = useViewportGate(t4);
  let t5;
  if ($[7] !== gate || $[8] !== props.src) {
    t5 = props.src !== undefined && gate.shouldRender ? toCustomGateway(props.src) : null;
    $[7] = gate;
    $[8] = props.src;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const baseLegacySrc = t5;
  const legacyRetry = useImageRetry(baseLegacySrc, props.retryOnError === true, props.onError);
  if (props.src === undefined && props.entity === undefined) {
    throw new Error("OptimizedImage: one of `src` or `entity` must be provided. See images-plan.md \xA7E4.");
  }
  if (props.src !== undefined && props.entity !== undefined) {
    throw new Error("OptimizedImage: `src` and `entity` are mutually exclusive.");
  }
  const t6 = props.alt;
  let t7;
  let t8;
  if ($[10] !== gate.containerRef || $[11] !== gate.shouldRender || $[12] !== handleLegacyReveal || $[13] !== legacyLoadState || $[14] !== legacyRetry || $[15] !== legacyRevealed || $[16] !== props.alt || $[17] !== props.blurDataURL || $[18] !== props.className || $[19] !== props.entity || $[20] !== props.forceRaster || $[21] !== props.imageClassName || $[22] !== props.imageOverride || $[23] !== props.loadReveal || $[24] !== props.nsfw || $[25] !== props.objectPosition || $[26] !== props.onError || $[27] !== props.onExhausted || $[28] !== props.preview || $[29] !== props.priority || $[30] !== props.retryOnError || $[31] !== props.size || $[32] !== props.sizes || $[33] !== props.suppressReveal || $[34] !== props.variant || $[35] !== reducedMotion || $[36] !== resolvedQuality || $[37] !== resolvedRounded || $[38] !== tImage) {
    t8 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const baseArgs = {
        alt: t6,
        resolvedQuality,
        resolvedRounded,
        ...(props.className !== undefined ? {
          className: props.className
        } : {}),
        ...(props.imageClassName !== undefined ? {
          imageClassName: props.imageClassName
        } : {}),
        ...(props.objectPosition !== undefined ? {
          objectPositionClass: `object-${props.objectPosition}`
        } : {}),
        ...(props.size !== undefined ? {
          size: props.size
        } : {}),
        ...(props.sizes !== undefined ? {
          sizes: props.sizes
        } : {}),
        ...(props.priority !== undefined ? {
          priority: props.priority
        } : {}),
        ...(props.blurDataURL !== undefined ? {
          blurDataURL: props.blurDataURL
        } : {}),
        ...(props.onError !== undefined ? {
          onError: props.onError
        } : {}),
        ...(props.preview !== undefined ? {
          preview: props.preview
        } : {}),
        previewLabel: tImage("preview"),
        shouldRender: gate.shouldRender,
        loadReveal: props.loadReveal ?? "default"
      };
      if (props.entity !== undefined) {
        t8 = <EntityDriven variant={props.variant} entity={props.entity} args={baseArgs} forceRaster={props.forceRaster === true} suppressReveal={props.suppressReveal === true} imageOverride={props.imageOverride} onExhausted={props.onExhausted} containerRef={gate.containerRef} />;
        break bb0;
      }
      if (process.env.NODE_ENV === "development") {
        sendLog("OptimizedImage: legacy `src` API used; migrate to `entity={...}`. See images-plan.md \xA7E4.", "warn", {
          context: "OptimizedImage.legacy-src"
        });
      }
      const legacySrc = legacyRetry.src;
      const retrying = props.retryOnError === true;
      let t9;
      if ($[41] !== legacyLoadState || $[42] !== legacyRetry || $[43] !== retrying) {
        t9 = retrying ? () => {
          legacyRetry.handleSuccess();
          legacyLoadState.handleLoad();
        } : legacyLoadState.handleLoad;
        $[41] = legacyLoadState;
        $[42] = legacyRetry;
        $[43] = retrying;
        $[44] = t9;
      } else {
        t9 = $[44];
      }
      const args = {
        ...baseArgs,
        src: legacySrc,
        loaded: legacyLoadState.loaded,
        instant: legacyLoadState.instant,
        handleLoad: t9,
        reducedMotion,
        nsfw: props.nsfw === true,
        revealed: legacyRevealed,
        onReveal: handleLegacyReveal,
        suppressReveal: props.suppressReveal === true,
        loadReveal: props.loadReveal ?? "default",
        ...(retrying ? {
          onError: legacyRetry.handleError
        } : {})
      };
      if (props.variant === "thumbnail") {
        t8 = renderThumbnail(args, legacyLoadState.imgRef, gate.containerRef);
        break bb0;
      }
      if (props.variant === "avatar") {
        t8 = renderAvatar(args, legacyLoadState.imgRef, gate.containerRef);
        break bb0;
      }
      t7 = renderHero(args, legacyLoadState.imgRef, gate.containerRef);
    }
    $[10] = gate.containerRef;
    $[11] = gate.shouldRender;
    $[12] = handleLegacyReveal;
    $[13] = legacyLoadState;
    $[14] = legacyRetry;
    $[15] = legacyRevealed;
    $[16] = props.alt;
    $[17] = props.blurDataURL;
    $[18] = props.className;
    $[19] = props.entity;
    $[20] = props.forceRaster;
    $[21] = props.imageClassName;
    $[22] = props.imageOverride;
    $[23] = props.loadReveal;
    $[24] = props.nsfw;
    $[25] = props.objectPosition;
    $[26] = props.onError;
    $[27] = props.onExhausted;
    $[28] = props.preview;
    $[29] = props.priority;
    $[30] = props.retryOnError;
    $[31] = props.size;
    $[32] = props.sizes;
    $[33] = props.suppressReveal;
    $[34] = props.variant;
    $[35] = reducedMotion;
    $[36] = resolvedQuality;
    $[37] = resolvedRounded;
    $[38] = tImage;
    $[39] = t7;
    $[40] = t8;
  } else {
    t7 = $[39];
    t8 = $[40];
  }
  if (t8 !== Symbol.for("react.early_return_sentinel")) {
    return t8;
  }
  return t7;
}